import { maybeRequirementValue } from "@Light/components/enroll/flow/screen.ts";
import { Button } from "@Light/components/page/Button";
import { PageBody } from "@Light/components/page/PageBody";
import { Spinner } from "@Light/components/system/Spinner";
import type { RequirementsPatch } from "@Light/services/lightTypes";
import { usePlan } from "@Light/utils/plan";
import { captureMessage } from "@sentry/react";
import { AlertCircle } from "@untitledui/icons";
import { useEffect, useRef } from "react";
import { EnrollHelpButtonFooter } from "./EnrollHelpButtonFooter";
import { useEnrollment } from "./flow/context";

export function planPatch(
  groupUuid: string,
  patchedGroupUuid: string | undefined,
): RequirementsPatch {
  return patchedGroupUuid === groupUuid
    ? {}
    : { plan: { group_uuid: groupUuid } };
}

export const PlanSelection = () => {
  const plan = usePlan();
  const { next, nextMutation, requirements } = useEnrollment();

  const groupUuid = plan.group.uuid;
  const maybePatchedGroupUuid = maybeRequirementValue(
    requirements.plan,
  )?.group_uuid;
  const attemptAutoAdvance = useRef(false);

  useEffect(() => {
    if (attemptAutoAdvance.current) return;
    attemptAutoAdvance.current = true;
    next(planPatch(groupUuid, maybePatchedGroupUuid), {
      replace: true,
      throwOnError: true,
    }).catch((error: unknown) => {
      captureMessage("Failed to auto-select enrollment plan", {
        level: "error",
        extra: { error, groupUuid, patchedGroupUuid: maybePatchedGroupUuid },
      });
    });
  }, [groupUuid, next, maybePatchedGroupUuid]);

  if (nextMutation.isError) {
    return (
      <PageBody
        centered
        icon={<AlertCircle className="size-8 text-quaternary" />}
        title="Something went wrong with selecting your plan"
        subtitle="Please try again, or reach out to our support team if the problem continues."
        footer={
          <EnrollHelpButtonFooter helpButtonText="Need help?">
            <Button
              size="lg"
              className="w-full"
              data-testid="plan-selection-retry"
              onClick={() => {
                void next(planPatch(groupUuid, maybePatchedGroupUuid), {
                  replace: true,
                });
              }}
            >
              Try again
            </Button>
          </EnrollHelpButtonFooter>
        }
      />
    );
  }

  return (
    <div className="flex items-center justify-center h-full">
      <Spinner className="w-8 h-8" />
    </div>
  );
};
